import { observable, action } from 'mobx';
import {
  initConnection,
  endConnection,
  getProducts,
  requestPurchase,
  finishTransaction,
  purchaseUpdatedListener,
  purchaseErrorListener,
} from 'react-native-iap';

import { validateAppleReceipt } from './apiCalls';
import { IS_IOS, IS_WEB } from './appConstants';
import logImport from './log';

const log = logImport as (name: string, params?: Record<string, any>) => void;

type IapProduct = {
  productId: string;
  price: string;
  localizedPrice: string;
  title: string;
  description: string;
  currency: string;
};

type PendingContext = {
  // Grant payload forwarded to validateAppleReceipt, e.g.
  // { buildingId, avatarId } for avatars or { buildingId, packId } for gold.
  payload: Record<string, any>;
  resolve?: () => void;
  reject?: (err: any) => void;
};

class IapStore {
  @observable ready = false;
  @observable products: IapProduct[] = [];
  @observable inFlight = false;

  private pending: Record<string, PendingContext> = {};
  private updateSub: { remove: () => void } | null = null;
  private errorSub: { remove: () => void } | null = null;

  @action
  async init(productIds: string[]) {
    if (!IS_IOS) return;
    try {
      await initConnection();
      const products = await getProducts({ skus: productIds });
      this.products = products as IapProduct[];

      this.updateSub = purchaseUpdatedListener(async (purchase: any) => {
        await this.handlePurchase(purchase);
      });
      this.errorSub = purchaseErrorListener((err: any) => {
        log('IapPurchaseError', { code: err.code, message: err.message });
        Object.keys(this.pending).forEach((pid) => {
          this.pending[pid].reject?.(err);
          delete this.pending[pid];
        });
        this.inFlight = false;
      });

      this.ready = true;
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.warn('iap.init failed:', e?.message || e);
      this.ready = false;
    }
  }

  async dispose() {
    this.updateSub?.remove();
    this.errorSub?.remove();
    this.updateSub = null;
    this.errorSub = null;
    try {
      await endConnection();
    } catch {}
  }

  getProduct(productId: string): IapProduct | undefined {
    return this.products.find((p) => p.productId === productId);
  }

  @action
  buy(productId: string, payload: Record<string, any>): Promise<void> {
    if (!this.ready || this.inFlight) return Promise.resolve();
    this.inFlight = true;
    return new Promise<void>((resolve, reject) => {
      this.pending[productId] = { payload, resolve, reject };
      requestPurchase({ sku: productId }).catch((e: any) => {
        this.inFlight = false;
        delete this.pending[productId];
        reject(e);
      });
    });
  }

  private async handlePurchase(purchase: any) {
    const ctx = this.pending[purchase.productId];
    if (!ctx) {
      await finishTransaction({ purchase, isConsumable: true });
      return;
    }
    const receipt = purchase.transactionReceipt;
    if (!receipt) return;
    try {
      await validateAppleReceipt({
        ...ctx.payload,
        productId: purchase.productId,
        receipt,
      });
      await finishTransaction({ purchase, isConsumable: true });
      log('IapPurchaseGranted', { productId: purchase.productId, ...ctx.payload });
      ctx.resolve?.();
    } catch (e: any) {
      log('IapValidateFailed', { productId: purchase.productId, message: e?.message });
      ctx.reject?.(e);
    } finally {
      delete this.pending[purchase.productId];
      this.inFlight = false;
    }
  }
}

export default new IapStore();
